// src/slices/ticketRecallAppraisal/reducer.ts
// Redux slice cho màn "Duyệt thu hồi vé ế" (mục 22/23, REQUIRE_FIXBUGS.md)

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TicketRecall } from 'pages/TicketRecall/types';
import {
  TicketRecallAppraisalFilters,
  TicketRecallAppraisalModalState,
  TicketRecallAppraisalState,
} from './types';
import {
  fetchAppraisalRecalls,
  fetchAppraisalRecallById,
  approveTicketRecall,
  rejectTicketRecall,
} from './thunk';

const initialFilters: TicketRecallAppraisalFilters = {
  // Mặc định hiện TOÀN BỘ danh sách phiếu thuộc phạm vi màn này — đã thẩm định (Đã duyệt/Từ chối)
  // hoặc chưa (Chờ thẩm định) — theo yêu cầu role admin, không chỉ riêng phiếu đang chờ xử lý.
  status: 'All',
  planId: '',
  agentId: '',
};

const initialState: TicketRecallAppraisalState = {
  list: [],
  selectedItem: null,
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 0,
  },
  filters: { ...initialFilters },
  loading: false,
  loadingDetail: false,
  submitting: false,
  error: null,
  modalState: {
    detail: false,
    approve: false,
    reject: false,
  },
};

const ticketRecallAppraisalSlice = createSlice({
  name: 'TicketRecallAppraisal',
  initialState,
  reducers: {
    setSelectedItem: (state, action: PayloadAction<TicketRecall | null>) => {
      state.selectedItem = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1;
    },
    setFilters: (state, action: PayloadAction<Partial<TicketRecallAppraisalFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = { ...initialFilters };
      state.pagination.currentPage = 1;
    },
    clearError: (state) => {
      state.error = null;
    },
    openModal: (state, action: PayloadAction<keyof TicketRecallAppraisalModalState>) => {
      state.modalState[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof TicketRecallAppraisalModalState>) => {
      state.modalState[action.payload] = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Danh sách phiếu
      .addCase(fetchAppraisalRecalls.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppraisalRecalls.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.list = payload?.items || payload?.data || payload || [];
        state.pagination.totalItems = payload?.totalItems ?? payload?.totalCount ?? state.list.length;
        state.pagination.totalPages =
          payload?.totalPages ??
          Math.ceil(state.pagination.totalItems / Math.max(state.pagination.pageSize, 1));
      })
      .addCase(fetchAppraisalRecalls.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Chi tiết phiếu
      .addCase(fetchAppraisalRecallById.pending, (state) => {
        state.loadingDetail = true;
      })
      .addCase(fetchAppraisalRecallById.fulfilled, (state, action) => {
        state.loadingDetail = false;
        const updated = action.payload as TicketRecall;
        state.selectedItem = updated;
        // Mục 13.b, README_UPDATE.md — sau khi Duyệt/Từ chối, phiếu vẫn phải ở lại đúng vị trí trong
        // danh sách đang xem, chỉ đổi badge trạng thái tại chỗ — không biến mất dù filter đang lọc
        // theo trạng thái cũ (khác với refetch cả trang, sẽ loại phiếu ra khỏi kết quả).
        const index = state.list.findIndex((item) => item.id === updated?.id);
        if (index !== -1) state.list[index] = updated;
      })
      .addCase(fetchAppraisalRecallById.rejected, (state, action) => {
        state.loadingDetail = false;
        state.error = action.payload as string;
      })

      // Duyệt / Từ chối — API chỉ trả id nên không thể cập nhật item tại chỗ, nơi gọi phải refetch
      // danh sách.
      .addCase(approveTicketRecall.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(approveTicketRecall.fulfilled, (state) => {
        state.submitting = false;
        state.modalState.approve = false;
        state.modalState.detail = false;
        state.selectedItem = null;
      })
      .addCase(approveTicketRecall.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      .addCase(rejectTicketRecall.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(rejectTicketRecall.fulfilled, (state) => {
        state.submitting = false;
        state.modalState.reject = false;
        state.modalState.detail = false;
        state.selectedItem = null;
      })
      .addCase(rejectTicketRecall.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedItem,
  setPage,
  setPageSize,
  setFilters,
  clearFilters,
  clearError,
  openModal,
  closeModal,
} = ticketRecallAppraisalSlice.actions;

export default ticketRecallAppraisalSlice.reducer;
